// ARXMENTIS i18n — lightweight English/Spanish provider used across the cockpit.
// Persists language choice in localStorage so admin and viewers keep their preference between sessions.
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const I18nContext = createContext(null);

const TRANSLATIONS = {
  en: {
    // Sidebar
    "nav.dashboard": "Dashboard",
    "nav.journal": "Decision Journal",
    "nav.log": "Cycle Tracker",
    "nav.market_scanner": "Market Scanner",
    "nav.forge": "Market Forge",
    "nav.performance": "Performance",
    "nav.leaderboard": "Leaderboard",
    "nav.settings": "Settings",
    "role.admin": "Admin",
    "role.viewer": "Viewer",
    "action.signout": "Sign Out",
    "lang.toggle": "Language",
    "lang.en": "ENG",
    "lang.es": "ESP",

    // Auth / setup
    "auth.title": "ARX MENTIS",
    "auth.subtitle": "Income Engine",
    "auth.tagline": "Premium Market Intelligence",
    "auth.google": "Sign in with Google",
    "auth.or_password": "or with password",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.enter": "Enter",
    "auth.access_denied": "Access denied",
    "setup.first_run": "FIRST RUN",
    "setup.create_admin": "Create Global Admin",
    "setup.note": "This is shown once. The credentials you enter become the permanent admin.",
    "setup.display_name": "Display Name",
    "setup.admin_email": "Admin Email",
    "setup.password": "Password (min 8)",
    "setup.confirm_password": "Confirm Password",
    "setup.submit": "Create Admin & Enter",

    // Common
    "common.refresh": "Refresh",
    "common.close": "Close",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.loading": "Loading…",
    "common.no_data": "No data yet.",
    "common.awaiting": "Awaiting data…",

    // Dashboard
    "dash.title": "Dashboard",
    "dash.subtitle": "ITM Wheel Scanner — Live Stream",
    "dash.add_ticker": "Add ticker",
    "dash.valid_only": "Valid Only",
    "dash.show_all": "Show All",
    "dash.high_conviction": "High Conviction",
    "dash.high_conviction_hint": "WES-P ≥ 7 · WES-C ≥ 7 · income ≥ 3 %",
    "dash.last_decision": "Last decision",
    "dash.outcome_pulse_hint": "Open Decision Journal",
    "dash.connected": "Connected",
    "dash.not_connected": "Not Connected",

    // Cycle Tracker / Misc page titles
    "page.cycle_tracker": "Cycle Tracker",
    "page.scanner": "Scanner",
    "page.market_scanner": "Market Scanner",
    "page.wes_trades": "WES Trades",
    "page.settings": "Settings",
    "page.leaderboard": "Leaderboard",

    // Market Forge — header
    "forge.title": "Market Forge",
    "forge.phase_badge": "PHASE 2",
    "forge.subtitle": "Chart · WES structure · Wheel ITM Decision Engine · ROI hard gate",
    "forge.tf_1h": "1 Hour",
    "forge.tf_daily": "Daily",
    "forge.snapshot": "Snapshot",
    "forge.snapshot_tooltip": "Save the current chart + WES + chain + decision as a journal snapshot",
    "forge.journal": "Journal",
    "forge.symbol_placeholder": "SPY",

    // Market Forge — left rail
    "forge.watchlist": "Watchlist",
    "forge.watchlist_empty": "Empty — add symbols on the Scanner page.",

    // Chart toolbar
    "forge.zoom_in": "Zoom in (or use mouse wheel)",
    "forge.zoom_out": "Zoom out",
    "forge.zoom_reset": "Reset zoom",
    "forge.chart_settings": "Chart settings",
    "forge.fullscreen": "Fullscreen",
    "forge.exit_fullscreen": "Exit fullscreen",
    "forge.bars_count": "bars",

    // Auto-refresh / live tick / source chip
    "forge.live": "LIVE",
    "forge.live_tick": "tick 5s",
    "forge.source_tradier": "Tradier",
    "forge.source_tastytrade": "Tastytrade",
    "forge.source_cache": "Cache",
    "forge.market_closed": "Market closed",
    "forge.last_tick": "Last tick",
    "forge.last_bar": "Last bar",
    "forge.stale": "stale",
    "forge.full_refresh": "full refresh",

    // Watchlist Scanner (Phase 4)
    "forge.scan_all": "Scan all",
    "forge.scanning": "Scanning…",
    "forge.ranked": "Ranked",
    "forge.alphabetical": "A–Z",
    "forge.cached": "cached",
    "forge.scan_errors": "errors",
    "forge.no_trade_short": "—",
    "forge.tradeable": "TRADE",
    "forge.last_scanned": "Last scan",

    // Replay Mode (Phase 5)
    "replay.toggle_tip": "Replay Mode — pick a past date and back-test the engine",
    "replay.title": "Replay Mode",
    "replay.as_of": "As of",
    "replay.dte": "DTE",
    "replay.run": "Run",
    "replay.running": "Running…",
    "replay.exit": "Exit replay",
    "replay.reveal": "Reveal future",
    "replay.hide": "Hide future",
    "replay.hypothetical": "Hypothetical Entry",
    "replay.atr_label": "ATR(14)",
    "replay.spot_at": "Spot at as-of",
    "replay.strike": "Strike (3% ITM)",
    "replay.premium": "Est. premium",
    "replay.breakeven": "Breakeven",
    "replay.roi": "Est. ROI",
    "replay.gate": "Gate",
    "replay.outcome": "Outcome at expiration",
    "replay.spot_at_exp": "Spot at exp",
    "replay.outcome_pending": "Future bars don't reach the projected expiration yet.",
    "replay.outcome_win": "WIN",
    "replay.outcome_loss": "LOSS",
    "replay.outcome_be": "BREAKEVEN",
    "replay.note_proxy": "Premium is an ATR-based proxy — directional, not exact.",

    // WES panel
    "forge.wes_structure": "WES Structure",
    "forge.bias": "Bias",
    "forge.combo": "Combo",
    "forge.signal": "Signal",
    "forge.signal_turning_point": "Turning Point",
    "forge.signal_continuation": "Continuation",

    // Decision panel
    "forge.decision": "Decision",
    "forge.evaluating": "Evaluating Wheel ITM Decision Engine…",
    "forge.label_pure": "TRADE — PURE WHEEL ITM",
    "forge.label_ladder": "TRADE — WHEEL LADDER ITM",
    "forge.label_no_trade": "NO TRADE",
    "forge.expiration": "Expiration",
    "forge.primary_k": "Primary K",
    "forge.secondary_k": "Secondary K",
    "forge.premium_bid": "Premium (bid)",
    "forge.roi_bid": "ROI (bid)",
    "forge.gate": "Gate",
    "forge.do": "DO",
    "forge.dont": "DON'T",
    "forge.pin_button": "Pin to Cycle Tracker…",

    // Iter116 — Consolidated Trade Verdict card
    "forge.trade_verdict": "Trade Verdict",
    "forge.candidate_strike": "Candidate Strike",
    "forge.gex_risk_readout": "GEX Risk Readout",
    "forge.dealer_regime": "Dealer Regime",
    "forge.flip_spot": "Flip · Spot",
    "forge.pin_risk": "Pin Risk",
    "forge.assignment_risk": "Assignment Risk",
    "forge.path_quality": "Path Quality",
    "forge.suggested_strikes": "Suggested Strikes",
    "forge.show_detailed_breakdown": "Show detailed breakdown",
    "forge.hide_detailed_breakdown": "Hide detailed breakdown",
    "forge.copy": "Copy",
    "forge.copied": "Copied",
    "forge.copy_verdict": "Copy verdict to clipboard",

    // Iter117 — Per-strategy verdict mini-cards (Market Forge)
    "scanner.bpcs_short": "BPCS",
    "scanner.bccs_short": "BCCS",
    "scanner.iron_condor_short": "Iron Condor",
    "scanner.cfm_shares_short": "CC · Shares",
    "scanner.cfm_leaps_short": "CC · LEAPS",

    // Chain table
    "forge.chain_title": "Options Chain · Puts (ITM band)",
    "forge.col_strike": "Strike",
    "forge.col_bid": "Bid",
    "forge.col_ask": "Ask",
    "forge.col_mid": "Mid",
    "forge.col_delta": "Δ",
    "forge.col_iv": "IV",
    "forge.col_oi": "OI",
    "forge.col_vol": "Vol",
    "forge.col_roi_bid": "ROI (bid)",
    "forge.col_roi_mid": "ROI (mid)",
    "forge.col_class": "Class",
    "forge.exp": "Exp",
    "forge.dte": "DTE",
    "forge.roi_gate": "ROI gate",
    "forge.strikes": "strikes",

    // Pin modal
    "pin.title": "Review & Pin to Cycle Tracker",
    "pin.symbol": "Symbol",
    "pin.spot_entry": "Spot at entry",
    "pin.strike": "Strike",
    "pin.strike_leg2": "Strike (Leg 2 — deeper ITM)",
    "pin.premium_fill": "Premium (you'll fill at)",
    "pin.suggested": "Suggested bid:",
    "pin.mid": "mid:",
    "pin.dte": "DTE",
    "pin.wes_p": "WES-P",
    "pin.wes_c": "WES-C",
    "pin.ladder_context": "Ladder context (read-only)",
    "pin.comment_label": "Comment / Journal note",
    "pin.computed": "Computed at submit",
    "pin.capital": "Capital required:",
    "pin.breakeven": "Breakeven:",
    "pin.roi_fill": "ROI on this fill:",
    "pin.confirm": "Confirm & Pin Cycle",
    "pin.pinning": "Pinning…",
    "pin.required": "Strike, Premium and DTE are required",
    "pin.error_default": "Could not create cycle",

    // Chart settings popover
    "chart.settings": "Chart Settings",
    "chart.vp_bins": "VP bin density",
    "chart.vp_warning": "Visual only — does NOT change WES math (engine uses 500 bins server-side).",
    "chart.va_highlight": "VA highlight",
    "chart.va_subtle": "Subtle",
    "chart.va_default": "Default",
    "chart.va_bold": "Bold",
    "chart.candle_theme": "Candle theme",
    "chart.theme_gr": "Green/Red",
    "chart.theme_bo": "Blue/Orange",
    "chart.theme_mono": "Mono",
    "chart.show_grid": "Show grid",
    "chart.show_vwap": "Session VWAP (1H only)",
    "chart.show_vwap_bands": "VWAP ±1σ / ±2σ bands",
    "chart.visible_range": "Visible range",
    "chart.right_offset": "Right offset (empty bars)",
    "chart.saved_local": "Settings are saved per-user in your browser.",

    // Snapshots / Decision Score Card
    "snap.title": "Decision Journal",
    "snap.score_card": "Decision Score Card",
    "snap.empty": "No snapshots yet. Click",
    "snap.empty_button_word": "Snapshot",
    "snap.empty_tail": "to record the current chart + WES + chain + decision.",
    "snap.col_when": "When",
    "snap.col_symbol": "Symbol",
    "snap.col_tf": "TF",
    "snap.col_spot": "Spot",
    "snap.col_wes": "WES-P / WES-C",
    "snap.col_decision": "Decision",
    "snap.col_roi": "ROI",
    "snap.col_outcome": "Outcome",
    "snap.col_note": "Note",
    "snap.col_actions": "Actions",
    "snap.replay": "Replay",
    "snap.export": "Export",
    "snap.export_title": "Open a one-page printable report (Print → Save as PDF).",

    // Phase 6 — Batch Backtest
    "batch.toggle_tip": "Batch backtest — multi-period Wheel ITM simulation",
    "batch.title": "Batch Backtest",
    "batch.cadence": "Cadence",
    "batch.weekly": "Weekly",
    "batch.monthly": "Monthly",
    "batch.weekday": "Weekday",
    "batch.start": "Start",
    "batch.end": "End",
    "batch.dte": "DTE",
    "batch.note": "Daily bars only. Pure Wheel ITM heuristic (3% ITM strike, ATR-based premium proxy).",
    "batch.run": "Run Backtest",
    "batch.running": "Running...",
    "batch.empty_prompt": "Set a date range and click Run Backtest to simulate.",
    "batch.stat_total": "Total",
    "batch.stat_wins": "Wins",
    "batch.stat_losses": "Losses",
    "batch.stat_be": "BreakEven",
    "batch.stat_winrate": "Win Rate",
    "batch.stat_avg_roi": "Avg ROI",
    "batch.stat_realized": "Realized P&L",
    "batch.stat_drawdown": "Max DD",
    "batch.stat_tradeable": "Tradeable",
    "batch.stat_tradeable_winrate": "Tradeable WR",
    "batch.col_date": "Date",
    "batch.col_spot": "Spot",
    "batch.col_strike": "Strike",
    "batch.col_premium": "Prem",
    "batch.col_roi": "ROI",
    "batch.col_spot_exp": "Spot@Exp",
    "batch.col_pnl": "P&L",
    "batch.col_outcome": "Outcome",
    "snap.outcome.pending": "Pending",
    "snap.outcome.win": "Win",
    "snap.outcome.loss": "Loss",
    "snap.outcome.breakeven": "BreakEven",
    "snap.outcome.expired": "Expired (auto)",
    "snap.outcome_save": "Save",
    "snap.auto_detect": "Auto-detect",
    "snap.auto_detect_tip": "Compare current spot to strike at expiration (CSP perspective).",
    "snap.outcome_notes_placeholder": "Outcome notes (optional)",
    "snap.summary_total": "Total",
    "snap.summary_wins": "Wins",
    "snap.summary_losses": "Losses",
    "snap.summary_be": "BreakEven",
    "snap.summary_pending": "Pending",
    "snap.summary_winrate": "Win rate",

    // Skip Reasons Leaderboard (Dashboard widget)
    "skip.title": "Skip Reasons",
    "skip.skipped_of": "skipped of",
    "skip.skip_rate": "skip rate",
    "skip.skip": "skip",
    "skip.skips": "skips",
    "skip.reason.wide_spread": "Spread too wide (illiquid options)",
    "skip.reason.low_oi": "Open interest below threshold",
    "skip.reason.below_min_price": "Premium too small",
    "skip.reason.low_liquidity": "Liquidity failed",
    "skip.reason.liquidity_failed_on_one_or_both_legs": "Liquidity failed (ladder leg)",
    "skip.reason.no_delta": "Missing delta data",
    "skip.reason.delta_out_of_range": "Delta outside 0.65-0.80 ITM band",
    "skip.reason.delta_out_of_band": "Delta outside acceptable band (ladder)",
    "skip.reason.roi_below_gate": "ROI below gate",
    "skip.reason.combined_roi_below_gate": "Combined ROI below gate (ladder)",
    "skip.reason.no_qualifying_chain_data": "No qualifying chain data",
    "skip.reason.unknown": "Unknown (older snapshot)",

    // GEX Panel
    "gex.title": "Gamma Exposure",
    "gex.subtitle": "market regime filter — modifies aggressiveness, never overrides ROI/WES",
    "gex.collapse": "Collapse",
    "gex.expand": "Expand",
    "gex.regime": "Regime",
    "gex.regime.long_gamma": "Long Gamma",
    "gex.regime.short_gamma": "Short Gamma",
    "gex.regime.transition": "Transition",
    "gex.regime.unknown": "—",
    "gex.zero_gamma": "Zero γ",
    "gex.call_wall": "Call wall",
    "gex.put_wall": "Put wall",
    "gex.spot": "Spot",
    "gex.candidate_strike": "Candidate strike",
    "gex.score": "Score",
    "gex.classification": "Class",
    "gex.class.PREMIUM_ROTATION_CSP": "Premium Rotation",
    "gex.class.NORMAL_CSP": "Normal CSP",
    "gex.class.NEUTRAL": "Neutral",
    "gex.class.ASSIGNMENT_ACCEPTED": "Assignment Accepted",
    "gex.class.DANGER_PREMIUM": "Danger Premium",
    "gex.class.NO_DATA": "No Data",
    "gex.sign_model": "Sign",
    "gex.sign.dealer_short": "Dealer",
    "gex.sign.raw": "Raw",
    "gex.scale_mode": "Scale",
    "gex.scale.raw": "Raw $",
    "gex.scale.percent_1": "Per 1%",
    "gex.loading": "Computing GEX…",
    "gex.error": "GEX unavailable",
    "gex.empty": "No GEX data for this chain.",
    "gex.warnings": "Notes",
    // GEX scorer note codes (locale-aware) — args interpolated client-side via {placeholder}
    "gex.note.spot_gex_long_gamma":           "Spot GEX is ${spot_gex} — dealer LONG GAMMA, compressive regime.",
    "gex.note.spot_gex_short_gamma":          "Spot GEX is ${spot_gex} — dealer SHORT GAMMA, expansive regime.",
    "gex.note.spot_gex_neutral":              "Spot GEX is ${spot_gex} — near zero, NEUTRAL / TRANSITION regime.",
    "gex.note.path_clean":                    "Path spot→strike is clean — no dominant wall in the way.",
    "gex.note.path_blocked":                  "Path spot→strike is blocked by a wall at ${blocking_strike} — expect friction.",
    "gex.note.path_mixed":                    "Path spot→strike is mixed — partial absorption around ${blocking_strike}.",
    "gex.note.call_wall_resistance":          "Call wall at ${call_wall} carries positive GEX — resistance / pinning risk.",
    "gex.note.call_wall_breakout_fuel":       "Call wall at ${call_wall} carries negative GEX — breakout fuel if broken.",
    "gex.note.put_wall_support":              "Put wall at ${put_wall} carries positive GEX — support / absorption risk.",
    "gex.note.put_wall_cascade":              "Put wall at ${put_wall} carries negative GEX — fragile support, cascade risk if broken.",
    "gex.note.pinning_risk_at_candidate":     "Candidate strike ${candidate_strike} sits on a positive gamma cluster — pinning risk.",
    "gex.note.call_wall_at_candidate_weak_wes": "Call wall (${call_wall}) sits at the candidate strike with weak WES — resistance risk.",
    // GEX history sparkline
    "gex.history": "γ flip drift",
    "gex.history.empty": "Collecting history…",
    "gex.fullscreen": "Open chart fullscreen",
    "gex.close": "Close",
    "gex.axis.put_compression": "put compression",
    "gex.axis.call_compression": "call compression",
    "gex.curve_legend": "Net γ vs price",
    "gex.strike.reset": "Reset strike",
    "gex.strike.reset_tooltip": "Return to the auto-picked candidate strike",
    "strategy_tracker.title": "Strategy Tracker",
    "strategy_tracker.snaps": "snapshots",
    "strategy_tracker.loading": "Loading tracker…",
    "strategy_tracker.empty": "Tracker unavailable",
    "strategy_tracker.collecting": "Collecting regime data…",
    "strategy_tracker.current": "Now",
    "strategy_tracker.dominant": "Dominant",
    "strategy_tracker.stability": "Stability",
    "strategy_tracker.transitions": "Transitions",
    "strategy_tracker.ago.mins": "{n}m ago",
    "strategy_tracker.ago.hrs": "{n}h ago",
    "strategy_tracker.ago.days": "{n}d ago",

    // WES Trades page
    "wes_trades.title": "WES Trades",
    "wes_trades.subtitle": "Hourly: WES-P ≥7 AND WES-C ≥7 AND CSP ≥3% — Daily for context",
    "wes_trades.actionable": "Actionable",
    "wes_trades.near_threshold": "Near Threshold",
    "wes_trades.scanning": "Scanning...",
    "wes_trades.empty": "No tickers meet all criteria",
    "wes_trades.tab_live": "Live Scan",
    "wes_trades.tab_journal": "Decision Journal",

    // Scanner page — REMOVED in Iter115 (legacy `/scanner` UI page
    // deleted; the surviving `/market-scanner` carries the workflow).

    // Market Scanner page
    "market_scanner.title": "Market Scanner",
    "market_scanner.subtitle": "Full universe sweep — WES + ROI ranked",
    "market_scanner.start_btn": "Start Scan",
    "market_scanner.cancel_btn": "Cancel",
    "market_scanner.running": "Running",
    "market_scanner.last_run": "Last run",
    "market_scanner.progress": "Progress",
    "market_scanner.results": "Results",
    "market_scanner.no_results": "No results yet. Click Start Scan to begin.",

    // Leaderboard page
    "leaderboard.title": "Family Leaderboard",
    "leaderboard.subtitle": "Decision Score Card — rolling 30 days",
    "leaderboard.col_user": "User",
    "leaderboard.col_total": "Total",
    "leaderboard.col_wins": "Wins",
    "leaderboard.col_losses": "Losses",
    "leaderboard.col_be": "BreakEven",
    "leaderboard.col_pending": "Pending",
    "leaderboard.col_winrate": "Win Rate",
    "leaderboard.col_avg_roi": "Avg ROI",
    "leaderboard.empty": "No snapshots yet.",

    // Settings tabs
    "settings.title": "Settings",
    "settings.tab_brokers": "Brokers",
    "settings.tab_strategy": "Strategy",
    "settings.tab_users": "Users",
    "settings.tab_scheduled": "Scheduled Scans",
    "settings.tab_session": "Session Activity",
    "settings.tab_general": "General",
    "settings.saved": "Saved",
    "settings.save": "Save",
    "settings.viewer_locked": "Broker keys are admin-only. You can still adjust your strategy knobs.",
  },
  es: {
    // Sidebar
    "nav.dashboard": "Panel",
    "nav.journal": "Diario de Decisiones",
    "nav.log": "Rastreador de Ciclos",
    "nav.market_scanner": "Escáner de Mercado",
    "nav.forge": "Forja de Mercado",
    "nav.performance": "Rendimiento",
    "nav.leaderboard": "Tabla de Posiciones",
    "nav.settings": "Configuración",
    "role.admin": "Admin",
    "role.viewer": "Visor",
    "action.signout": "Cerrar Sesión",
    "lang.toggle": "Idioma",
    "lang.en": "ENG",
    "lang.es": "ESP",

    // Auth / setup
    "auth.title": "ARX MENTIS",
    "auth.subtitle": "Motor de Ingresos",
    "auth.tagline": "Inteligencia de Mercado Premium",
    "auth.google": "Iniciar sesión con Google",
    "auth.or_password": "o con contraseña",
    "auth.email": "Correo",
    "auth.password": "Contraseña",
    "auth.enter": "Entrar",
    "auth.access_denied": "Acceso denegado",
    "setup.first_run": "PRIMERA EJECUCIÓN",
    "setup.create_admin": "Crear Administrador Global",
    "setup.note": "Esto se muestra una sola vez. Las credenciales que ingreses serán el administrador permanente.",
    "setup.display_name": "Nombre Visible",
    "setup.admin_email": "Correo del Administrador",
    "setup.password": "Contraseña (mín. 8)",
    "setup.confirm_password": "Confirmar Contraseña",
    "setup.submit": "Crear Admin e Ingresar",

    // Common
    "common.refresh": "Actualizar",
    "common.close": "Cerrar",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.loading": "Cargando…",
    "common.no_data": "Aún no hay datos.",
    "common.awaiting": "Esperando datos…",

    // Dashboard
    "dash.title": "Panel",
    "dash.subtitle": "Escáner Wheel ITM — Flujo en Vivo",
    "dash.add_ticker": "Agregar ticker",
    "dash.valid_only": "Solo Válidos",
    "dash.show_all": "Ver Todos",
    "dash.high_conviction": "Alta Convicción",
    "dash.high_conviction_hint": "WES-P ≥ 7 · WES-C ≥ 7 · ingreso ≥ 3 %",
    "dash.last_decision": "Última decisión",
    "dash.outcome_pulse_hint": "Abrir Diario de Decisiones",
    "dash.connected": "Conectado",
    "dash.not_connected": "Sin Conexión",

    // Page titles
    "page.cycle_tracker": "Rastreador de Ciclos",
    "page.scanner": "Escáner",
    "page.market_scanner": "Escáner de Mercado",
    "page.wes_trades": "Operaciones WES",
    "page.settings": "Configuración",
    "page.leaderboard": "Tabla de Posiciones",

    // Market Forge — header
    "forge.title": "Forja de Mercado",
    "forge.phase_badge": "FASE 2",
    "forge.subtitle": "Gráfico · Estructura WES · Motor de Decisión Wheel ITM · Filtro ROI",
    "forge.tf_1h": "1 Hora",
    "forge.tf_daily": "Diario",
    "forge.snapshot": "Captura",
    "forge.snapshot_tooltip": "Guarda el gráfico actual + WES + cadena + decisión como entrada del diario",
    "forge.journal": "Diario",
    "forge.symbol_placeholder": "SPY",

    // Market Forge — left rail
    "forge.watchlist": "Lista de Seguimiento",
    "forge.watchlist_empty": "Vacía — agrega símbolos en la página del Escáner.",

    // Chart toolbar
    "forge.zoom_in": "Acercar (o usa la rueda del ratón)",
    "forge.zoom_out": "Alejar",
    "forge.zoom_reset": "Restablecer zoom",
    "forge.chart_settings": "Ajustes del gráfico",
    "forge.fullscreen": "Pantalla completa",
    "forge.exit_fullscreen": "Salir de pantalla completa",
    "forge.bars_count": "barras",

    // Auto-refresh / live tick / source chip
    "forge.live": "EN VIVO",
    "forge.live_tick": "tick 5s",
    "forge.source_tradier": "Tradier",
    "forge.source_tastytrade": "Tastytrade",
    "forge.source_cache": "Caché",
    "forge.market_closed": "Mercado cerrado",
    "forge.last_tick": "Último tick",
    "forge.last_bar": "Última barra",
    "forge.stale": "obsoleto",
    "forge.full_refresh": "actualización completa",

    // Watchlist Scanner (Phase 4)
    "forge.scan_all": "Escanear todo",
    "forge.scanning": "Escaneando…",
    "forge.ranked": "Por rango",
    "forge.alphabetical": "A–Z",
    "forge.cached": "en caché",
    "forge.scan_errors": "errores",
    "forge.no_trade_short": "—",
    "forge.tradeable": "OPERAR",
    "forge.last_scanned": "Último escaneo",

    // Replay Mode (Phase 5)
    "replay.toggle_tip": "Modo Replay — elige una fecha pasada y prueba el motor",
    "replay.title": "Modo Replay",
    "replay.as_of": "Al",
    "replay.dte": "DTE",
    "replay.run": "Ejecutar",
    "replay.running": "Ejecutando…",
    "replay.exit": "Salir del replay",
    "replay.reveal": "Mostrar futuro",
    "replay.hide": "Ocultar futuro",
    "replay.hypothetical": "Entrada Hipotética",
    "replay.atr_label": "ATR(14)",
    "replay.spot_at": "Spot en la fecha",
    "replay.strike": "Strike (3% ITM)",
    "replay.premium": "Prima estimada",
    "replay.breakeven": "Punto de equilibrio",
    "replay.roi": "ROI estimado",
    "replay.gate": "Filtro",
    "replay.outcome": "Resultado al vencimiento",
    "replay.spot_at_exp": "Spot al vencimiento",
    "replay.outcome_pending": "Las barras futuras aún no llegan al vencimiento proyectado.",
    "replay.outcome_win": "GANADA",
    "replay.outcome_loss": "PERDIDA",
    "replay.outcome_be": "EMPATE",
    "replay.note_proxy": "La prima es una aproximación basada en ATR — direccional, no exacta.",

    // WES panel
    "forge.wes_structure": "Estructura WES",
    "forge.bias": "Sesgo",
    "forge.combo": "Combo",
    "forge.signal": "Señal",
    "forge.signal_turning_point": "Punto de Giro",
    "forge.signal_continuation": "Continuación",

    // Decision panel
    "forge.decision": "Decisión",
    "forge.evaluating": "Evaluando Motor de Decisión Wheel ITM…",
    "forge.label_pure": "OPERAR — WHEEL ITM PURO",
    "forge.label_ladder": "OPERAR — WHEEL LADDER ITM",
    "forge.label_no_trade": "NO OPERAR",
    "forge.expiration": "Vencimiento",
    "forge.primary_k": "K Principal",
    "forge.secondary_k": "K Secundario",
    "forge.premium_bid": "Prima (bid)",
    "forge.roi_bid": "ROI (bid)",
    "forge.gate": "Filtro",
    "forge.do": "HACER",
    "forge.dont": "NO HACER",
    "forge.pin_button": "Fijar al Rastreador de Ciclos…",

    // Iter116 — Tarjeta consolidada Veredicto del Trade
    "forge.trade_verdict": "Veredicto del Trade",
    "forge.candidate_strike": "Strike Candidato",
    "forge.gex_risk_readout": "Lectura de Riesgo GEX",
    "forge.dealer_regime": "Régimen Dealer",
    "forge.flip_spot": "Flip · Spot",
    "forge.pin_risk": "Riesgo Pin",
    "forge.assignment_risk": "Riesgo Asignación",
    "forge.path_quality": "Calidad de Trayectoria",
    "forge.suggested_strikes": "Strikes Sugeridos",
    "forge.show_detailed_breakdown": "Ver desglose detallado",
    "forge.hide_detailed_breakdown": "Ocultar desglose detallado",
    "forge.copy": "Copiar",
    "forge.copied": "Copiado",
    "forge.copy_verdict": "Copiar veredicto al portapapeles",

    // Iter117 — Mini-tarjetas Veredicto por Estrategia (Market Forge)
    "scanner.bpcs_short": "BPCS",
    "scanner.bccs_short": "BCCS",
    "scanner.iron_condor_short": "Iron Condor",
    "scanner.cfm_shares_short": "CC · Acciones",
    "scanner.cfm_leaps_short": "CC · LEAPS",

    // Chain table
    "forge.chain_title": "Cadena de Opciones · Puts (banda ITM)",
    "forge.col_strike": "Strike",
    "forge.col_bid": "Bid",
    "forge.col_ask": "Ask",
    "forge.col_mid": "Med",
    "forge.col_delta": "Δ",
    "forge.col_iv": "IV",
    "forge.col_oi": "OI",
    "forge.col_vol": "Vol",
    "forge.col_roi_bid": "ROI (bid)",
    "forge.col_roi_mid": "ROI (med)",
    "forge.col_class": "Clase",
    "forge.exp": "Venc",
    "forge.dte": "DTE",
    "forge.roi_gate": "Filtro ROI",
    "forge.strikes": "strikes",

    // Pin modal
    "pin.title": "Revisar y Fijar al Rastreador de Ciclos",
    "pin.symbol": "Símbolo",
    "pin.spot_entry": "Spot al entrar",
    "pin.strike": "Strike",
    "pin.strike_leg2": "Strike (Pierna 2 — más ITM)",
    "pin.premium_fill": "Prima (precio de llenado)",
    "pin.suggested": "Bid sugerido:",
    "pin.mid": "med:",
    "pin.dte": "DTE",
    "pin.wes_p": "WES-P",
    "pin.wes_c": "WES-C",
    "pin.ladder_context": "Contexto Ladder (solo lectura)",
    "pin.comment_label": "Comentario / Nota del diario",
    "pin.computed": "Calculado al enviar",
    "pin.capital": "Capital requerido:",
    "pin.breakeven": "Punto de equilibrio:",
    "pin.roi_fill": "ROI a este precio:",
    "pin.confirm": "Confirmar y Fijar Ciclo",
    "pin.pinning": "Fijando…",
    "pin.required": "Strike, Prima y DTE son obligatorios",
    "pin.error_default": "No se pudo crear el ciclo",

    // Chart settings popover
    "chart.settings": "Ajustes del Gráfico",
    "chart.vp_bins": "Densidad de bins de VP",
    "chart.vp_warning": "Solo visual — NO cambia la matemática WES (el motor usa 500 bins en el servidor).",
    "chart.va_highlight": "Resalte VA",
    "chart.va_subtle": "Sutil",
    "chart.va_default": "Predet.",
    "chart.va_bold": "Marcado",
    "chart.candle_theme": "Tema de velas",
    "chart.theme_gr": "Verde/Rojo",
    "chart.theme_bo": "Azul/Naranja",
    "chart.theme_mono": "Mono",
    "chart.show_grid": "Mostrar cuadrícula",
    "chart.show_vwap": "VWAP de sesión (solo 1H)",
    "chart.show_vwap_bands": "Bandas VWAP ±1σ / ±2σ",
    "chart.visible_range": "Rango visible",
    "chart.right_offset": "Margen derecho (barras vacías)",
    "chart.saved_local": "Los ajustes se guardan por usuario en tu navegador.",

    // Snapshots / Decision Score Card
    "snap.title": "Diario de Decisiones",
    "snap.score_card": "Tarjeta de Puntuación",
    "snap.empty": "Aún no hay capturas. Haz clic en",
    "snap.empty_button_word": "Captura",
    "snap.empty_tail": "para registrar el gráfico + WES + cadena + decisión actuales.",
    "snap.col_when": "Cuándo",
    "snap.col_symbol": "Símbolo",
    "snap.col_tf": "TF",
    "snap.col_spot": "Spot",
    "snap.col_wes": "WES-P / WES-C",
    "snap.col_decision": "Decisión",
    "snap.col_roi": "ROI",
    "snap.col_outcome": "Resultado",
    "snap.col_note": "Nota",
    "snap.col_actions": "Acciones",
    "snap.replay": "Reproducir",
    "snap.export": "Exportar",
    "snap.export_title": "Abrir un reporte imprimible de una página (Imprimir → Guardar como PDF).",

    // Phase 6 — Batch Backtest
    "batch.toggle_tip": "Backtest por lotes — simulación multi-periodo de Wheel ITM",
    "batch.title": "Backtest por Lotes",
    "batch.cadence": "Frecuencia",
    "batch.weekly": "Semanal",
    "batch.monthly": "Mensual",
    "batch.weekday": "Día",
    "batch.start": "Inicio",
    "batch.end": "Fin",
    "batch.dte": "DTE",
    "batch.note": "Solo barras diarias. Heurística Pure Wheel ITM (strike 3% ITM, prima basada en ATR).",
    "batch.run": "Ejecutar Backtest",
    "batch.running": "Ejecutando...",
    "batch.empty_prompt": "Selecciona un rango y haz clic en Ejecutar Backtest para simular.",
    "batch.stat_total": "Total",
    "batch.stat_wins": "Ganadas",
    "batch.stat_losses": "Perdidas",
    "batch.stat_be": "Empates",
    "batch.stat_winrate": "Tasa Aciertos",
    "batch.stat_avg_roi": "ROI Prom.",
    "batch.stat_realized": "P&L Real.",
    "batch.stat_drawdown": "DD Máx",
    "batch.stat_tradeable": "Operables",
    "batch.stat_tradeable_winrate": "TA Op.",
    "batch.col_date": "Fecha",
    "batch.col_spot": "Spot",
    "batch.col_strike": "Strike",
    "batch.col_premium": "Prima",
    "batch.col_roi": "ROI",
    "batch.col_spot_exp": "Spot@Venc",
    "batch.col_pnl": "P&L",
    "batch.col_outcome": "Resultado",
    "snap.outcome.pending": "Pendiente",
    "snap.outcome.win": "Ganada",
    "snap.outcome.loss": "Perdida",
    "snap.outcome.breakeven": "Empate",
    "snap.outcome.expired": "Vencida (auto)",
    "snap.outcome_save": "Guardar",
    "snap.auto_detect": "Auto-detectar",
    "snap.auto_detect_tip": "Compara el spot actual con el strike al vencimiento (perspectiva CSP).",
    "snap.outcome_notes_placeholder": "Notas del resultado (opcional)",
    "snap.summary_total": "Total",
    "snap.summary_wins": "Ganadas",
    "snap.summary_losses": "Perdidas",
    "snap.summary_be": "Empates",
    "snap.summary_pending": "Pendientes",
    "snap.summary_winrate": "Tasa de aciertos",

    // Skip Reasons Leaderboard (Dashboard widget)
    "skip.title": "Razones de no operar",
    "skip.skipped_of": "omitidos de",
    "skip.skip_rate": "tasa de omisión",
    "skip.skip": "omisión",
    "skip.skips": "omisiones",
    "skip.reason.wide_spread": "Spread demasiado amplio (opciones ilíquidas)",
    "skip.reason.low_oi": "Open interest bajo el umbral",
    "skip.reason.below_min_price": "Prima demasiado pequeña",
    "skip.reason.low_liquidity": "Liquidez insuficiente",
    "skip.reason.liquidity_failed_on_one_or_both_legs": "Liquidez insuficiente (pierna de ladder)",
    "skip.reason.no_delta": "Datos de delta faltantes",
    "skip.reason.delta_out_of_range": "Delta fuera del rango ITM 0.65-0.80",
    "skip.reason.delta_out_of_band": "Delta fuera del rango aceptable (ladder)",
    "skip.reason.roi_below_gate": "ROI por debajo del umbral",
    "skip.reason.combined_roi_below_gate": "ROI combinado por debajo del umbral (ladder)",
    "skip.reason.no_qualifying_chain_data": "Sin datos de cadena calificados",
    "skip.reason.unknown": "Desconocido (snapshot antiguo)",

    // GEX Panel
    "gex.title": "Exposición Gamma",
    "gex.subtitle": "filtro de régimen — modula agresividad, nunca anula ROI/WES",
    "gex.collapse": "Colapsar",
    "gex.expand": "Expandir",
    "gex.regime": "Régimen",
    "gex.regime.long_gamma": "Gamma Largo",
    "gex.regime.short_gamma": "Gamma Corto",
    "gex.regime.transition": "Transición",
    "gex.regime.unknown": "—",
    "gex.zero_gamma": "γ Cero",
    "gex.call_wall": "Muro Call",
    "gex.put_wall": "Muro Put",
    "gex.spot": "Spot",
    "gex.candidate_strike": "Strike candidato",
    "gex.score": "Puntaje",
    "gex.classification": "Clase",
    "gex.class.PREMIUM_ROTATION_CSP": "Rotación de Prima",
    "gex.class.NORMAL_CSP": "CSP Normal",
    "gex.class.NEUTRAL": "Neutral",
    "gex.class.ASSIGNMENT_ACCEPTED": "Asignación Aceptada",
    "gex.class.DANGER_PREMIUM": "Prima Peligrosa",
    "gex.class.NO_DATA": "Sin Datos",
    "gex.sign_model": "Signo",
    "gex.sign.dealer_short": "Dealer",
    "gex.sign.raw": "Bruto",
    "gex.scale_mode": "Escala",
    "gex.scale.raw": "$ Bruto",
    "gex.scale.percent_1": "Por 1%",
    "gex.loading": "Calculando GEX…",
    "gex.error": "GEX no disponible",
    "gex.empty": "Sin datos GEX para esta cadena.",
    "gex.warnings": "Notas",
    // GEX scorer note codes (locale-aware) — args interpolated client-side via {placeholder}
    "gex.note.spot_gex_long_gamma":           "Spot GEX es ${spot_gex} — dealer LONG GAMMA, régimen compresivo.",
    "gex.note.spot_gex_short_gamma":          "Spot GEX es ${spot_gex} — dealer SHORT GAMMA, régimen expansivo.",
    "gex.note.spot_gex_neutral":              "Spot GEX es ${spot_gex} — cerca de cero, régimen NEUTRAL / TRANSICIÓN.",
    "gex.note.path_clean":                    "Camino spot→strike despejado — sin muro dominante en el camino.",
    "gex.note.path_blocked":                  "Camino spot→strike bloqueado por muro en ${blocking_strike} — habrá fricción.",
    "gex.note.path_mixed":                    "Camino spot→strike mixto — absorción parcial cerca de ${blocking_strike}.",
    "gex.note.call_wall_resistance":          "Muro call en ${call_wall} con GEX positivo — riesgo de resistencia / pinning.",
    "gex.note.call_wall_breakout_fuel":       "Muro call en ${call_wall} con GEX negativo — combustible para ruptura si rompe.",
    "gex.note.put_wall_support":              "Muro put en ${put_wall} con GEX positivo — riesgo de soporte / absorción.",
    "gex.note.put_wall_cascade":              "Muro put en ${put_wall} con GEX negativo — soporte frágil, riesgo de cascada si rompe.",
    "gex.note.pinning_risk_at_candidate":     "Strike candidato ${candidate_strike} sobre cluster gamma positivo — riesgo de pinning.",
    "gex.note.call_wall_at_candidate_weak_wes": "Muro call (${call_wall}) coincide con strike candidato y WES débil — riesgo de resistencia.",
    // GEX history sparkline
    "gex.history": "Deriva del γ flip",
    "gex.history.empty": "Recolectando historial…",
    "gex.fullscreen": "Abrir gráfico en pantalla completa",
    "gex.close": "Cerrar",
    "gex.axis.put_compression": "compresión put",
    "gex.axis.call_compression": "compresión call",
    "gex.curve_legend": "γ neto vs precio",
    "gex.strike.reset": "Restablecer strike",
    "gex.strike.reset_tooltip": "Volver al strike candidato automático",
    "strategy_tracker.title": "Rastreador de Estrategia",
    "strategy_tracker.snaps": "snapshots",
    "strategy_tracker.loading": "Cargando rastreador…",
    "strategy_tracker.empty": "Rastreador no disponible",
    "strategy_tracker.collecting": "Recolectando datos de régimen…",
    "strategy_tracker.current": "Ahora",
    "strategy_tracker.dominant": "Dominante",
    "strategy_tracker.stability": "Estabilidad",
    "strategy_tracker.transitions": "Transiciones",
    "strategy_tracker.ago.mins": "hace {n}m",
    "strategy_tracker.ago.hrs": "hace {n}h",
    "strategy_tracker.ago.days": "hace {n}d",

    // WES Trades page
    "wes_trades.title": "Operaciones WES",
    "wes_trades.subtitle": "Por hora: WES-P ≥7 Y WES-C ≥7 Y CSP ≥3% — Diario para contexto",
    "wes_trades.actionable": "Accionables",
    "wes_trades.near_threshold": "Cerca del Umbral",
    "wes_trades.scanning": "Escaneando...",
    "wes_trades.empty": "Ningún ticker cumple todos los criterios",
    "wes_trades.tab_live": "Escaneo en Vivo",
    "wes_trades.tab_journal": "Diario de Decisiones",

    // Scanner page — REMOVED in Iter115.

    // Market Scanner page
    "market_scanner.title": "Escáner de Mercado",
    "market_scanner.subtitle": "Barrido completo del universo — ordenado por WES + ROI",
    "market_scanner.start_btn": "Iniciar Escaneo",
    "market_scanner.cancel_btn": "Cancelar",
    "market_scanner.running": "En Ejecución",
    "market_scanner.last_run": "Última ejecución",
    "market_scanner.progress": "Progreso",
    "market_scanner.results": "Resultados",
    "market_scanner.no_results": "Sin resultados aún. Haz clic en Iniciar Escaneo para comenzar.",

    // Leaderboard page
    "leaderboard.title": "Tabla Familiar",
    "leaderboard.subtitle": "Boletín de Decisiones — últimos 30 días",
    "leaderboard.col_user": "Usuario",
    "leaderboard.col_total": "Total",
    "leaderboard.col_wins": "Ganadas",
    "leaderboard.col_losses": "Perdidas",
    "leaderboard.col_be": "Empates",
    "leaderboard.col_pending": "Pendientes",
    "leaderboard.col_winrate": "Tasa de Aciertos",
    "leaderboard.col_avg_roi": "ROI Promedio",
    "leaderboard.empty": "Sin snapshots todavía.",

    // Settings tabs
    "settings.title": "Configuración",
    "settings.tab_brokers": "Brokers",
    "settings.tab_strategy": "Estrategia",
    "settings.tab_users": "Usuarios",
    "settings.tab_scheduled": "Escaneos Programados",
    "settings.tab_session": "Actividad de Sesión",
    "settings.tab_general": "General",
    "settings.saved": "Guardado",
    "settings.save": "Guardar",
    "settings.viewer_locked": "Las claves de broker son solo para administradores. Aún puedes ajustar tus parámetros de estrategia.",
  },
};

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem("arx.lang") || "en"; } catch { return "en"; }
  });
  useEffect(() => {
    try { localStorage.setItem("arx.lang", lang); } catch {}
  }, [lang]);

  const setLang = (next) => {
    if (next === "en" || next === "es") setLangState(next);
  };

  const value = useMemo(() => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
    const t = (key, fallback) => {
      if (Object.prototype.hasOwnProperty.call(dict, key)) return dict[key];
      if (TRANSLATIONS.en[key] !== undefined) return TRANSLATIONS.en[key];
      return fallback != null ? fallback : key;
    };
    return { lang, setLang, t };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // graceful no-op fallback so components don't crash if mounted outside provider
    return { lang: "en", setLang: () => {}, t: (k, f) => (f != null ? f : k) };
  }
  return ctx;
}

export function LanguageToggle({ className = "", testid = "language-toggle" }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div data-testid={testid} className={`inline-flex items-center gap-0 rounded-sm border border-[#26262B] overflow-hidden ${className}`}>
      <button
        type="button"
        data-testid="language-toggle-en"
        onClick={() => setLang("en")}
        className={`text-[10px] font-bold tracking-[0.18em] px-2 py-1 transition-colors ${lang === "en" ? "bg-[#00FF88]/10 text-[#00FF88]" : "text-[#8A8A93] hover:text-white"}`}
        title={t("lang.toggle")}
      >
        {t("lang.en")}
      </button>
      <button
        type="button"
        data-testid="language-toggle-es"
        onClick={() => setLang("es")}
        className={`text-[10px] font-bold tracking-[0.18em] px-2 py-1 border-l border-[#26262B] transition-colors ${lang === "es" ? "bg-[#00FF88]/10 text-[#00FF88]" : "text-[#8A8A93] hover:text-white"}`}
        title={t("lang.toggle")}
      >
        {t("lang.es")}
      </button>
    </div>
  );
}

export default I18nContext;

